/* eslint-disable func-names */
/* eslint-disable no-undef */
import React, { useState, useEffect } from 'react';
import * as Yup from 'yup';
import { withFormik, Field } from 'formik';
import { Form, Button, Dropdown, Menu } from 'semantic-ui-react';
import { withLocalize } from 'react-localize-redux';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { isEmpty } from 'lodash';
import axios from 'axios';
import styled from 'styled-components';
import HelpIcon from '../common/helpIcon';
import {
  HHStreamUrl,
  HHInput,
  HHFileUploader,
  HHTinyMCE,
  HHDatePicker,
  HHSingleSelect,
  HHVideoUploader,
  HHCheckbox
} from './fields';
import { getTimeZoneOptions } from '../common/datetime';
import { hlog } from '../common/util';
import * as API from '../network/api.js';
import { handleResponseCode } from '../actions/index';

const formikEnhancer = withFormik({
  validationSchema: ({ translate }) =>
    Yup.object().shape({
      coverUrl: Yup.string(),
      coverTempUrl: Yup.string().required(translate('form.error.mixed.required')),
      platform: Yup.string(),
      hostNames: Yup.string()
        .max(64, translate('form.error.string.max'))
        .required(translate('form.error.mixed.required')),
      timeZone: Yup.string().required(translate('form.error.mixed.required')),
      startTime: Yup.mixed()
        .moment()
        .required(translate('form.error.mixed.required')),
      endTime: Yup.mixed()
        .moment()
        .test(
          'afterStartDate',
          translate('form.error.moment.afterStartDate'),
          function (value) {
            const { startTime } = this.parent;
            return value.isAfter(startTime);
          }
        )
        .required(translate('form.error.mixed.required')),
      title: Yup.string().required(translate('form.error.mixed.required')),
      uploadedVideoUrl: Yup.string(),
      description: Yup.string()
        .max(1024, translate('form.error.string.max'))
        .required(translate('form.error.mixed.required')),
      streamingUrl: Yup.string(),
      streamingKey: Yup.string(),
      isPublic: Yup.boolean(),
      fileKey: Yup.string(),
      previewUrl: Yup.string()
    }),
  mapPropsToValues: props => props.initialValues,
  handleSubmit: (values, {
    props, setSubmitting, resetForm, setErrors
  }) => {
    const { translate } = props;
    if (values.fileKey === '' && (values.streamingUrl === '' || values.zoomUrl === '')) {
      setErrors({ uploadedVideoUrl: translate('form.error.mixed.required') });
      setErrors({ streamingUrl: translate('form.error.mixed.required') });
      setSubmitting(false);
      return;
    }
    props.onSubmit(values).then(() => {
      setSubmitting(false);
      resetForm();
      if (props.handleClose) {
        props.handleClose();
      }
    });
  }
});


const LiveStreamingForm = props => {
  const {
    handleClose,
    handleSubmit,
    isSubmitting,
    isEditing,
    translate,
    values,
    eventSlug,
    uploadedVideoKeys,
    user
  } = props;


  const [selectedVideo, setSelectedVideo] = useState('');

  const getPreviewLink = async (fileKey) => {
    const { user, eventSlug } = props;
    const { token } = user;

    try {
      const res = await axios.post(API.FETCH_VIDEO_PREVIEW_LINK, {
        token,
        eventSlug,
        fileKey,
        streamingId: 0
      });
      const { code, response } = res.data;
      handleResponseCode(code);
      props.setFieldValue('previewUrl', response.url);
    } catch (err) {
      hlog(err);
    }
  };

  useEffect(() => {
    if (!isEmpty(uploadedVideoKeys)) {
      const currentKey = uploadedVideoKeys.find(option => option.value === values.fileKey);
      if (currentKey) {
        setSelectedVideo(currentKey.value);
        getPreviewLink(currentKey.value);
      }
    }
    if (values.fileKey && isEmpty(values.previewUrl)) {
      getPreviewLink(values.fileKey);
    }
  }, []);

  const onChange = (e, { value }) => {
    setSelectedVideo(value);
    getPreviewLink(value);
    props.setFieldValue('fileKey', value);
  };

  let showUrl = true;

  let isCNDomain = false;
  const domain = window.location.href;
  if (domain.includes('event.hackhub.cn')) {
    isCNDomain = true;
  }
  let langCode = isCNDomain ? 'zh-Hans' : 'en';
  if (localStorage.getItem('language') === 'zh-Hans') {
    langCode = 'zh-Hans';
  }

  if (isEditing) {
    showUrl = false;
  }

  return (
    <Form onSubmit={handleSubmit} className='cms-resources_table_form'>
      <div className='cms-resources_table_form_formGroup'>
        <Form.Field width={16}>
          <label htmlFor='coverUrl'>{translate('streamCover')}</label>
          <Field
            name='coverUrl'
            component={HHFileUploader}
            otherProps={{
              id: 'coverUrl',
              type: 'image',
              fileType: 'event_streaming_thumbnail',
              slug: eventSlug,
              userToken: user.token,
              trigger: (
                <Button
                  primary basic type='button'
                  color='blue'
                >
                  {translate('streamCover')}
                </Button>
              )
            }}
          />
          {values.coverTempUrl && (
            <img
              src={values.coverTempUrl}
              alt='Stream Cover Background'
              className='mb1 upload-img-edit'
            />
          )}
        </Form.Field>

        <Form.Field width={16}>
          <label htmlFor='hostNames'>{translate('addHost')}</label>
          <Field
            name='hostNames'
            component={HHInput}
            otherProps={{
              type: 'text',
              id: 'hostNames'
            }}
          />
        </Form.Field>

        <Form.Field name='position-timezone'>
          <label htmlFor='timeZone'>{translate('chooseTimeZone')}</label>
          <Field
            name='timeZone'
            component={HHSingleSelect}
            otherProps={{
              id: 'form-timezone',
              simpleValue: true,
              searchable: true,
              clearable: false,
              options: getTimeZoneOptions(),
              placeholder: translate('timezone')
            }}
          />
        </Form.Field>

        <Form.Field>
          <label htmlFor='startTime'>{translate('startTime')}</label>
          <Field
            name='startTime'
            component={HHDatePicker}
            otherProps={{
              id: 'form-startTime',
              locale: langCode,
              placeholderText: translate('select start date')
            }}
          />
        </Form.Field>

        <Form.Field>
          <label htmlFor='endTime'>{translate('endTime')}</label>
          <Field
            name='endTime'
            component={HHDatePicker}
            type='livestreaming'
            otherProps={{
              id: 'form-endTime',
              locale: langCode,
              placeholderText: translate('select start date')
            }}
          />
        </Form.Field>

        <Form.Field width={16}>
          <label htmlFor='title'>{translate('addTitle')}</label>
          <Field
            name='title'
            component={HHInput}
            otherProps={{
              type: 'text',
              id: 'title'
            }}
          />
        </Form.Field>

        <Form.Field>
          <label htmlFor='uploadedVideoUrl'>{translate('uploadVideo')}</label>
          <Field
            // name='uploadedVideoUrl'
            name='previewUrl'
            component={HHVideoUploader}
            getPreviewLink={getPreviewLink}
            otherProps={{
              id: 'uploadedVideoUrl',
              type: 'video',
              eventSlug,
              trigger: (
                <Button
                  primary basic type='button'
                  color='blue'
                >
                  {translate('liveVideo')}
                </Button>
              )
            }}
          />
        </Form.Field>
        {
          !isEmpty(uploadedVideoKeys) && (
            <Form.Field>
              <Field
                name='uploadedVideoUrl' render={() => (
                  <Menu compact>
                    <Dropdown
                      simple item options={uploadedVideoKeys}
                      text={isEmpty(selectedVideo) ? 'Select Video' : selectedVideo.text}
                      value={selectedVideo} onChange={onChange}
                    />
                  </Menu>
                )}
              />
            </Form.Field>
          )
        }

        {
          !isEmpty(values.previewUrl) && (
          <Form.Field>
            <Field
              name='previewUrl'
              render={() => (
                <VideoWrapper>
                  <video
                    key={values.previewUrl}
                    controlsList='nodownload nofullscreen' muted playsInline
                    controls
                  >
                    <source src={`${values.previewUrl}#t=0,30`} type='video/mp4' />
                    Sorry, Your browser do not support embedded videos.
                  </video>
                </VideoWrapper>
              )}
            />
          </Form.Field>
          )
        }

        <Form.Field>
          <Field type='hidden' name='fileKey' value={values.fileKey} />
        </Form.Field>

        <Form.Field>
          <label htmlFor='streamingUrl'>{translate('liveUrl')}
            <HelpIcon content='Please contact our tech team! Temporarily only we can use ZOOM to host the meeting.' />
          </label>
          <Field
            name='streamingUrl'
            component={HHStreamUrl}
            endTime={values.endTime}
            eventSlug={eventSlug}
            showUrl={showUrl}
            otherProps={{
              type: 'text',
              id: 'form-streamingUrl',
              values
            }}
          />
        </Form.Field>

        <Form.Field>
          <label>{translate('Description')}</label>
          <Field
            name='description' component={HHTinyMCE}
            fileType='event_streaming_description'
            slug={eventSlug}
            userToken={user.token}
          />
        </Form.Field>

        <Form.Field>
          <label>{translate('public')}</label>
          <Field
            name='isPublic'
            component={HHCheckbox}
            otherProps={{ toggle: true }}
          />
        </Form.Field>
      </div>

      <div className='cms-resources_table_form_btnGroup'>
        {handleClose && (
          <Button basic type='button' onClick={handleClose}>
            {translate('cancel')}
          </Button>
        )}
        <Button
          primary
          disabled={isSubmitting}
          loading={isSubmitting}
          type='submit'
        >
          {isEditing ? translate('save') : translate('create')}
        </Button>
      </div>
    </Form>
  );
};

const VideoWrapper = styled.div`
  width: 400px;
  height: 300px;
  margin: 6px 0;

  video {
    width: 100%;
    height: 100%;
  }

  @media only screen and (max-width: 400px) {
    width: auto;
    height: auto;
  }
`;

export default compose(
  withLocalize,
  formikEnhancer,
  connect(({ user }) => ({ user }))
)(LiveStreamingForm);



// WEBPACK FOOTER //
// ./src/components/forms/liveStreamingForm.js